import { conversationUserNames, rosterWithoutCurrentUser } from '../utils/messaging/conversation';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import Component from '@glimmer/component';
import LinkButton from 'ember-simplepractice/components/spds/action/button/link';
import styles from './conversation-header.module.scss';

export default class ConversationHeader extends Component {
  @service('spds/toast') toast;
  @service clientProfileNavigator;
  @service intl;
  @service messaging;
  @service messagingNavigation;

  get conversation() {
    return this.args.conversation;
  }

  get filteredUsers() {
    return this.args.filteredUsers;
  }

  get isGroup() {
    return this.conversation.isGroup;
  }

  get memberCount() {
    return this.conversation.collapsedMembers.length;
  }

  get participantNames() {
    const currentUser = this.messaging.currentProfile?.user;
    const isDirectCouple = this.conversation.isCouple && !this.isGroup;
    // A direct couple can end up in previousMembers if disabled.
    const members = isDirectCouple
      ? [...this.conversation.collapsedMembers, ...this.conversation.previousMembers]
      : this.conversation.collapsedMembers;
    const visible = rosterWithoutCurrentUser(members, currentUser);

    return conversationUserNames(
      visible.map(member => member.name),
      isDirectCouple
    );
  }

  get shouldShowClientProfileLink() {
    return this.clientProfileNavigator.canNavigateToClientProfile(
      this.filteredUsers,
      this.messaging.currentProfile.isClinician
    );
  }

  navigateToClientProfileTask = task({ drop: true }, async () => {
    try {
      this.clientProfileNavigator.navigateToClientProfile(this.conversation, this.filteredUsers);
    } catch (error) {
      this.toast.error(this.intl.t('emc.general_error'));
    }
  });

  handleClientProfileClick = () => {
    document.activeElement.blur();
    this.navigateToClientProfileTask.perform();
  };

  handleGroupHeaderClick = () => {
    this.messagingNavigation.toConversationDetails();
  };

  <template>
    <div class={{styles.component}}>
      <span class="participant-names-container">
        {{#if this.isGroup}}
          <button
            type="button"
            class="group-message-header"
            aria-label={{t "emc.conversation_details.view_aria_label"}}
            data-test-group-message-header
            {{on "click" this.handleGroupHeaderClick}}
          >
            <span class="participant-names" data-test-group-message-title>
              {{t "emc.group_message"}}
            </span>
            <span class="member-count" data-test-group-message-member-count>
              {{t "emc.group_message_member_count" count=this.memberCount}}
            </span>
          </button>
        {{else}}
          {{#if this.shouldShowClientProfileLink}}
            <LinkButton
              {{on "click" this.handleClientProfileClick}}
              class="participant-names"
              data-test-client-profile-link
            >
              <span class="participant-names-text">
                {{this.participantNames}}
              </span>
            </LinkButton>
          {{else}}
            <span class="participant-names">
              <span class="participant-names-text">
                {{this.participantNames}}
              </span>
            </span>
          {{/if}}
        {{/if}}

        {{#if @showAwayIndicator}}
          <span class="away-indicator" data-test-away-indicator>
            {{t "emc.away_status.indicator"}}
          </span>
        {{/if}}
      </span>
    </div>
  </template>
}
